import axios from "axios";
import { getCloudFunctionUrl } from "../../utils/cloudFunctions";
import {
  AI_COMPETITORS_ENDPOINTS,
  AI_COMPETITORS_ERROR_INVALID_URL,
  AI_COMPETITORS_ERROR_NO_COMPETITORS,
} from "./aiCompetitorsConstants";

const ANALYZE_ENDPOINT = "analyzeInsightsCompetitorWebsite";
const ANALYZE_TIMEOUT_MS = 60000;
const ANALYZE_RETRY_DELAY_MS = 1000;

const NON_RETRYABLE_ANALYZE_CODES = new Set([
  AI_COMPETITORS_ERROR_INVALID_URL,
  AI_COMPETITORS_ERROR_NO_COMPETITORS,
]);

const waitMs = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const postAiCompetitorsFunction = async (endpoint, data, timeout) => {
  const { data: response } = await axios({
    method: "post",
    url: `${getCloudFunctionUrl(endpoint)}/${endpoint}`,
    data,
    timeout,
  });
  return response;
};

const isRetryableAnalyzeResult = (result) =>
  Boolean(result) &&
  result.ok === false &&
  !NON_RETRYABLE_ANALYZE_CODES.has(result.code);

const describeBodyShape = (value) => {
  if (value == null) return { type: "empty" };
  if (Array.isArray(value)) return { type: "array", size: value.length };
  if (typeof value !== "object") return { type: typeof value };
  return { type: "object", size: Object.keys(value).length };
};

const logAnalyzeCall = ({ attempt, request, response, error }) => {
  const responseBody = error ? error?.response?.data : response;
  console.log("[analyzeInsightsCompetitorWebsite]", {
    attempt,
    failed: Boolean(error),
    status: error?.response?.status || null,
    code: responseBody?.code || error?.code || null,
    requestShape: describeBodyShape(request),
    responseShape: describeBodyShape(responseBody),
  });
};

const postAnalyzeWebsite = async (payload, attempt) => {
  try {
    const response = await postAiCompetitorsFunction(
      ANALYZE_ENDPOINT,
      payload,
      ANALYZE_TIMEOUT_MS
    );
    logAnalyzeCall({ attempt, request: payload, response });
    return response;
  } catch (error) {
    logAnalyzeCall({ attempt, request: payload, error });
    throw error;
  }
};

export const pingAiCompetitorsEndpoints = () => {
  AI_COMPETITORS_ENDPOINTS.forEach((endpoint) => {
    postAiCompetitorsFunction(endpoint, { ping: true }, 8000).catch(() => {});
  });
};

export const createAiCompetitorsSession = async ({
  sessionId,
  language,
  createIfMissing = true,
}) =>
  postAiCompetitorsFunction("createInsightsCompetitorSession", {
    sessionId,
    language,
    createIfMissing,
  });

export const analyzeAiCompetitorsWebsite = async ({
  sessionId,
  websiteUrl,
  language,
}) => {
  const payload = { sessionId, websiteUrl, language };

  try {
    const firstResponse = await postAnalyzeWebsite(payload, 1);
    if (!isRetryableAnalyzeResult(firstResponse)) {
      return firstResponse;
    }
    console.log("[analyzeInsightsCompetitorWebsite] retrying after 1s", {
      requestShape: describeBodyShape(payload),
      responseShape: describeBodyShape(firstResponse),
      code: firstResponse?.code || null,
    });
  } catch (error) {
    console.log("[analyzeInsightsCompetitorWebsite] retrying after 1s", {
      requestShape: describeBodyShape(payload),
      code: error?.code || error?.response?.data?.code || null,
      status: error?.response?.status || null,
    });
  }

  await waitMs(ANALYZE_RETRY_DELAY_MS);
  return postAnalyzeWebsite(payload, 2);
};

export const searchAiCompetitorsAds = async ({
  sessionId,
  competitors,
  language,
}) =>
  postAiCompetitorsFunction(
    "searchInsightsCompetitorAds",
    { sessionId, competitors, language },
    290000
  );

export const updateAiCompetitorsSession = async ({
  sessionId,
  competitors,
  booking,
}) =>
  postAiCompetitorsFunction("updateInsightsCompetitorSession", {
    sessionId,
    competitors,
    booking,
  });
